import React, { useContext, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { X } from 'lucide-react';
import clip from '../../../../assets/production-flow/rnc/clip.svg';
import {
  AttachmentListActions,
  AttachmentListContainer,
  AttachmentListIconButton,
  AttachmentListLinkButton,
  AttachmentListName,
  AttachmentListRow,
} from '../../../../components/AttachmentManager/style';
import AttachmentSourceModal from '../../../../components/AttachmentSourceModal';
import AttachmentGalleryModal from '../../../../components/AttachmentGalleryModal';
import { GalleryFile } from '../../../../services/AttachmentApiService';
import AttachmentApiService from '../../../../services/AttachmentApiService';
import openSignedAttachment from '../../../../utils/openSignedAttachment';
import { downloadRemoteFile } from '../../../../utils/downloadRemoteFile';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import { financialEntryApi } from '../api';
import { COMPETENCE_MONTH_OPTIONS } from '../constants';
import { FinancialEntryFormSchemaType } from './schema';
import {
  AttachmentClipLabel,
  AttachmentCount,
  AttachmentHeaderBar,
  CompetenceFieldsRow,
  CompetenceGroup,
  StyledFormGroup,
} from './style';

const ItemFields: React.FC = () => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FinancialEntryFormSchemaType>();
  const { setFeedback } = useContext(FeedbackContext);
  const [isSourceModalOpen, setIsSourceModalOpen] = useState(false);
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const items = watch('items');
  const item = items?.[0];
  const itemId = item?.id;
  const attachments = item?.attachments ?? [];
  const pendingFiles = item?.pendingFiles ?? [];
  const pendingGalleryFiles = item?.pendingGalleryFiles ?? [];
  const totalFiles = attachments.length + pendingFiles.length + pendingGalleryFiles.length;
  const itemError = errors.items?.[0];

  const handleFilePick = (fileList: FileList | null) => {
    if (!fileList?.length) return;
    const newPending = Array.from(fileList).map((file) => ({
      file,
      previewName: file.name,
    }));
    setValue('items.0.pendingFiles', [...pendingFiles, ...newPending]);
  };

  const removePendingFile = (fileIndex: number) => {
    setValue(
      'items.0.pendingFiles',
      pendingFiles.filter((_, index) => index !== fileIndex),
    );
  };

  const handleSelectFromGallery = (files: GalleryFile[]) => {
    const existingGallery = item?.pendingGalleryFiles ?? [];
    const newFiles = files.filter(
      (f) => !existingGallery.some((eg: any) => eg.id === f.id)
    );
    setValue('items.0.pendingGalleryFiles', [...existingGallery, ...newFiles]);
  };

  const removePendingGalleryFile = (fileIndex: number) => {
    setValue(
      'items.0.pendingGalleryFiles',
      pendingGalleryFiles.filter((_, index) => index !== fileIndex)
    );
  };

  const openSignedUrl = async (
    fileId: number,
    options?: { downloadName?: string },
  ) => {
    if (!itemId) return;
    const url = options?.downloadName
      ? await financialEntryApi.downloadFile(itemId, fileId)
      : await financialEntryApi.viewFile(itemId, fileId);
    if (!url) return;
    if (options?.downloadName) {
      await downloadRemoteFile(url, options.downloadName);
      return;
    }
    await openSignedAttachment(url);
  };

  const handleView = async (fileId: number) => {
    try {
      await openSignedUrl(fileId);
    } catch {
      setFeedback({
        message: 'Erro ao visualizar arquivo.',
        isOpen: true,
        statusOk: false,
      });
    }
  };

  const handleDownload = async (fileId: number, name: string) => {
    try {
      await openSignedUrl(fileId, { downloadName: name });
    } catch {
      setFeedback({
        message: 'Erro ao baixar arquivo.',
        isOpen: true,
        statusOk: false,
      });
    }
  };

  const handleViewPending = (file: File) => {
    const url = URL.createObjectURL(file);
    window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const handleDownloadPending = (file: File) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDeleteFile = async (fileId: number) => {
    if (!itemId) return;
    try {
      await financialEntryApi.deleteFile(itemId, fileId);
      setValue(
        'items.0.attachments',
        attachments.filter((attachment) => attachment.id !== fileId),
      );
      setFeedback({
        message: 'Arquivo removido.',
        isOpen: true,
        statusOk: true,
      });
    } catch {
      setFeedback({
        message: 'Erro ao remover arquivo.',
        isOpen: true,
        statusOk: false,
      });
    }
  };

  return (
    <>
      <StyledFormGroup>
        <label>BU</label>
        <Controller
          name="items.0.businessUnit"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              value={field.value ?? ''}
              data-field-error={itemError?.businessUnit ? 'true' : undefined}
            />
          )}
        />
      </StyledFormGroup>

      <StyledFormGroup>
        <label>Projeto</label>
        <Controller
          name="items.0.project"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              value={field.value ?? ''}
              data-field-error={itemError?.project ? 'true' : undefined}
            />
          )}
        />
      </StyledFormGroup>

      <CompetenceGroup>
        <label>Competência</label>
        <CompetenceFieldsRow>
          <Controller
            name="items.0.competenceMonth"
            control={control}
            render={({ field }) => (
              <select
                {...field}
                value={field.value ?? ''}
                aria-label="Mês da competência"
                data-field-error={itemError?.competenceMonth ? 'true' : undefined}
              >
                <option value="">Mês</option>
                {COMPETENCE_MONTH_OPTIONS.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            )}
          />
          <Controller
            name="items.0.competenceYear"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                value={field.value ?? ''}
                maxLength={4}
                inputMode="numeric"
                placeholder="Ano"
                aria-label="Ano da competência"
                data-field-error={itemError?.competenceYear ? 'true' : undefined}
                onChange={(event) =>
                  field.onChange(
                    event.target.value.replace(/\D/g, '').slice(0, 4),
                  )
                }
              />
            )}
          />
        </CompetenceFieldsRow>
      </CompetenceGroup>

      <StyledFormGroup>
        <label>Anexo</label>
        <AttachmentHeaderBar>
          <AttachmentCount>{totalFiles} arquivo(s)</AttachmentCount>
          <AttachmentClipLabel
            as="button"
            type="button"
            onClick={() => setIsSourceModalOpen(true)}
            style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
          >
            <img src={clip} alt="Anexar arquivo" width={16} height={16} />
          </AttachmentClipLabel>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(event) => {
              handleFilePick(event.target.files);
              event.target.value = '';
            }}
          />
        </AttachmentHeaderBar>

        {totalFiles > 0 && (
          <AttachmentListContainer>
            {attachments.map((file) => {
              const fileName =
                file.originalName || file.fileName || file.title || 'arquivo';

              return (
                <AttachmentListRow key={file.id}>
                  <AttachmentListName title={fileName}>
                    {fileName}
                  </AttachmentListName>
                  <AttachmentListActions>
                    <AttachmentListLinkButton
                      type="button"
                      onClick={() => handleView(file.id)}
                    >
                      Visualizar
                    </AttachmentListLinkButton>
                    <AttachmentListLinkButton
                      type="button"
                      onClick={() => handleDownload(file.id, fileName)}
                    >
                      Download
                    </AttachmentListLinkButton>
                    <AttachmentListIconButton
                      type="button"
                      onClick={() => handleDeleteFile(file.id)}
                      title="Remover"
                    >
                      <X size={14} />
                    </AttachmentListIconButton>
                  </AttachmentListActions>
                </AttachmentListRow>
              );
            })}

            {pendingFiles.map((pendingFile, pendingIndex) => (
              <AttachmentListRow key={`pending-${pendingIndex}`}>
                <AttachmentListName title={pendingFile.previewName}>
                  {pendingFile.previewName}
                </AttachmentListName>
                <AttachmentListActions>
                  <AttachmentListLinkButton
                    type="button"
                    onClick={() => handleViewPending(pendingFile.file)}
                  >
                    Visualizar
                  </AttachmentListLinkButton>
                  <AttachmentListLinkButton
                    type="button"
                    onClick={() => handleDownloadPending(pendingFile.file)}
                  >
                    Download
                  </AttachmentListLinkButton>
                  <AttachmentListIconButton
                    type="button"
                    onClick={() => removePendingFile(pendingIndex)}
                    title="Remover"
                  >
                    <X size={14} />
                  </AttachmentListIconButton>
                </AttachmentListActions>
              </AttachmentListRow>
            ))}

            {pendingGalleryFiles.map((galleryFile: any, galleryIndex: number) => {
              const fileName = galleryFile.originalName || galleryFile.title || galleryFile.fileName || 'arquivo';
              return (
                <AttachmentListRow key={`gallery-${galleryFile.id || galleryIndex}`}>
                  <AttachmentListName title={fileName}>
                    {fileName}
                  </AttachmentListName>
                  <AttachmentListActions>
                    <AttachmentListLinkButton
                      type="button"
                      onClick={() => openSignedAttachment(galleryFile.fileUrl || galleryFile.signedUrl)}
                    >
                      Visualizar
                    </AttachmentListLinkButton>
                    <AttachmentListLinkButton
                      type="button"
                      onClick={async () => {
                        const url = await AttachmentApiService.getDownloadUrl(galleryFile.id);
                        const fileName = galleryFile.title || galleryFile.originalName || galleryFile.fileName || 'anexo';
                        if (url) await downloadRemoteFile(url, fileName);
                      }}
                    >
                      Download
                    </AttachmentListLinkButton>
                    <AttachmentListIconButton
                      type="button"
                      onClick={() => removePendingGalleryFile(galleryIndex)}
                      title="Remover"
                    >
                      <X size={14} />
                    </AttachmentListIconButton>
                  </AttachmentListActions>
                </AttachmentListRow>
              );
            })}
          </AttachmentListContainer>
        )}
      </StyledFormGroup>

      <AttachmentSourceModal
        isOpen={isSourceModalOpen}
        onClose={() => setIsSourceModalOpen(false)}
        onChooseUpload={() => fileInputRef.current?.click()}
        onChooseGallery={() => setIsGalleryModalOpen(true)}
      />

      <AttachmentGalleryModal
        isOpen={isGalleryModalOpen}
        onClose={() => setIsGalleryModalOpen(false)}
        onSelect={handleSelectFromGallery}
        multiple={true}
      />
    </>
  );
};

export default ItemFields;
